import messageType from './messageType';

/**
 * 訊息列表的單一合併／去重規則。
 *
 * 為什麼需要這支：訊息會從三個地方進到列表 —— socket 推送、離線 buffer 在重連時補推、以及
 * 重連後的補拉。原本的去重只比 `receiptId`，而 `receiptId` **只有真人客服（`_User`）的訊息才有**；
 * bot（`AddOn`）和顧客的訊息都沒有。也就是說在同一條對話裡三分之二的訊息沒有這個鍵。
 *
 * 更要緊的是原本的比較沒有做存在性檢查，`undefined === undefined` 會讓兩筆不相干的訊息被判成同一筆。
 * 目前沒爆出重複問題純粹是靠這個巧合在擋 —— 一旦多一條插入路徑（補拉就是），它就會反過來變成
 * 重複來源。所以主鍵改用 `objectId`（歷史 API 與 socket payload 兩邊都穩定有），`receiptId` 只
 * 保留原本的用途：把本地樂觀訊息換成 server 版本，而且要求兩邊都有值才算命中。
 */

export interface ChatMessage {
	objectId?: string;
	receiptId?: string;
	contentType?: string;
	createdAt?: string | Date;
	data?: any;
	isPending?: boolean;
	error?: boolean;
	[key: string]: any;
}

function normalizeCreatedAt(message: ChatMessage): ChatMessage {
	return message?.createdAt ? message : { ...message, createdAt: new Date().toISOString() };
}

function getReceiptId(message: ChatMessage): string | undefined {
	return message?.receiptId || message?.data?.receiptId;
}

/** 兩邊都要有值才算命中；缺一邊就不是同一筆。 */
function sameReceiptId(one: ChatMessage, two: ChatMessage): boolean {
	const oneReceiptId = getReceiptId(one);
	const twoReceiptId = getReceiptId(two);
	return Boolean(oneReceiptId) && Boolean(twoReceiptId) && oneReceiptId === twoReceiptId;
}

function sameObjectId(one: ChatMessage, two: ChatMessage): boolean {
	return Boolean(one?.objectId) && Boolean(two?.objectId) && one.objectId === two.objectId;
}

/**
 * 用 incoming 更新既有的那一筆。`image-set` 的 url 沿用既有值以外的處理都以 incoming 為準，
 * 並且清掉 `isPending` / `error`（訊息既然被 server 認了，就不該還顯示成傳送中或失敗）。
 */
function reconcile(current: ChatMessage, incoming: ChatMessage): ChatMessage {
	const { isPending, error, ...restCurrent } = current;
	const nextData =
		current.contentType === messageType.IMAGE_SET
			? { ...current.data, url: incoming?.data?.url }
			: incoming.data !== undefined
			? incoming.data
			: current.data;

	return {
		...restCurrent,
		...(incoming.receiptId ? { receiptId: incoming.receiptId } : {}),
		objectId: incoming.objectId || current.objectId,
		createdAt: incoming.createdAt || current.createdAt,
		data: nextData,
	};
}

/** 把一筆訊息併進列表：命中既有的就原地更新，否則追加。 */
export function mergeMessage(currentMessages: ChatMessage[], incomingMessage: ChatMessage): ChatMessage[] {
	const normalized = normalizeCreatedAt(incomingMessage);
	let matchedIndex = currentMessages.findIndex(current => sameObjectId(current, normalized));

	if (matchedIndex === -1) {
		// objectId 對不上才退回 receiptId —— 這條專門處理「本地樂觀訊息還沒有 objectId」的情況。
		matchedIndex = currentMessages.findIndex(current => sameReceiptId(current, normalized));
	}

	if (matchedIndex === -1) {
		return [...currentMessages, normalized];
	}

	const nextMessages = [...currentMessages];
	nextMessages[matchedIndex] = reconcile(nextMessages[matchedIndex], normalized);
	return nextMessages;
}

/** 一次併進多筆（補拉、往上翻頁、buffer 補推都走這條）。 */
export function mergeMessages(currentMessages: ChatMessage[], incomingMessages: ChatMessage[]): ChatMessage[] {
	return (incomingMessages || []).reduce((accumulated, incoming) => mergeMessage(accumulated, incoming), currentMessages || []);
}

/**
 * 補拉完成後對帳本地的樂觀訊息。
 *
 * server 的資料是權威來源。補拉已經認掉的那些會在 mergeMessages 裡被換成 server 版本、`isPending`
 * 也一併清掉；這裡處理的是**剩下對不上的那些**。它們標成 `error` 而不是直接移除 —— 讓顧客打過的字
 * 靜默消失，正是這次要修的那類抱怨。`getMessageStyle` 已經會把 `error` 畫成半透明，不用動 UI。
 */
export function settlePendingMessages(messages: ChatMessage[]): ChatMessage[] {
	return (messages || []).map(message => (message?.isPending ? { ...message, isPending: false, error: true } : message));
}

/**
 * 取補拉的 since 基準：只看 server 認過（有 objectId）的訊息。
 *
 * 本地樂觀訊息的時間戳來自顧客自己的裝置時鐘。裝置快幾分鐘就會把基準推過還沒收到的訊息，
 * 那些訊息會被永久跳過 —— 比原本的問題更糟，而且現場看起來像隨機發生。
 */
export function findResyncSince(messages: ChatMessage[]): string | undefined {
	const newest = (messages || [])
		.filter(message => Boolean(message?.objectId) && Boolean(message?.createdAt))
		.map(message => new Date(message.createdAt as string | Date).valueOf())
		.filter(timestamp => !Number.isNaN(timestamp))
		.reduce(
			(max: number | undefined, timestamp) => (max === undefined || timestamp > max ? timestamp : max),
			undefined as number | undefined
		);

	return newest === undefined ? undefined : new Date(newest).toISOString();
}
